import "../../../css/LlmSettings.css";
import React, { useState, useEffect, useCallback } from "react";
import { Switch, Input, InputNumber, Button, Spin, message, Select } from "antd";
import {
  getLlmSettings, updateLlmSettings, getAttributes,
  getRestrictedTerms, addRestrictedTerm, deleteRestrictedTerm,
  getCompetitorTerms, addCompetitorTerm, deleteCompetitorTerm,
  getAllowedTerms, addAllowedTerm, deleteAllowedTerm,
  getOutOfScopeResponse, updateOutOfScopeResponse,
} from "../../../services/polly/llmSettingsService";

const { TextArea } = Input;
const { Option } = Select;

export default function LlmSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [attributes, setAttributes] = useState([]);
  const [restrictedTerms, setRestrictedTerms] = useState([]);
  const [competitorTerms, setCompetitorTerms] = useState([]);
  const [newRestricted, setNewRestricted] = useState({ term: '', reason: '' });
  const [newCompetitor, setNewCompetitor] = useState({ term: '', reason: '' });
  const [allowedTerms, setAllowedTerms] = useState([]);
  const [newAllowed, setNewAllowed] = useState({ term: '', reason: '' });
  const [outOfScopeText, setOutOfScopeText] = useState('');
  const [outOfScopeDirty, setOutOfScopeDirty] = useState(false);
  const [savingOutOfScope, setSavingOutOfScope] = useState(false);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getLlmSettings();
      setSettings(res);
    } catch (err) {
      if (err?.response?.status === 404) {
        setSettings({
          llmEnabled: false,
          llmToneVoice: '',
          llmResponseLength: 'concise',
          maxMessageLength: 2500,
          maxConversationMessages: 10,
          commercialConsentAttributeId: null,
          ragSimilarityThreshold: null,
          maxRagChunks: null,
        });
      } else {
        message.error("Failed to load settings");
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchSettings();
    getAttributes()
      .then((res) => setAttributes(Array.isArray(res) ? res : []))
      .catch(() => setAttributes([]));
    getRestrictedTerms()
      .then((res) => setRestrictedTerms(Array.isArray(res) ? res : []))
      .catch(() => setRestrictedTerms([]));
    getCompetitorTerms()
      .then((res) => setCompetitorTerms(Array.isArray(res) ? res : []))
      .catch(() => setCompetitorTerms([]));
    getAllowedTerms()
      .then((res) => setAllowedTerms(Array.isArray(res) ? res : []))
      .catch(() => setAllowedTerms([]));
    getOutOfScopeResponse()
      .then((res) => setOutOfScopeText(res?.text || ''))
      .catch(() => setOutOfScopeText(''));
  }, [fetchSettings]);

  const update = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await updateLlmSettings(settings);
      setSettings(res);
      setDirty(false);
      message.success("Settings saved");
    } catch (err) {
      message.error("Failed to save settings");
    }
    setSaving(false);
  };

  const handleSaveOutOfScope = async () => {
    setSavingOutOfScope(true);
    try {
      await updateOutOfScopeResponse(outOfScopeText);
      setOutOfScopeDirty(false);
      message.success("Out-of-scope response saved");
    } catch (err) {
      message.error("Failed to save out-of-scope response");
    }
    setSavingOutOfScope(false);
  };

  const handleAddRestricted = async () => {
    const term = newRestricted.term.trim();
    if (!term) return;
    try {
      const entry = await addRestrictedTerm(term, newRestricted.reason.trim() || null);
      setRestrictedTerms((prev) => [...prev, entry]);
      setNewRestricted({ term: '', reason: '' });
      message.success("Restricted term added");
    } catch (err) {
      message.error("Failed to add restricted term");
    }
  };

  const handleRemoveRestricted = async (rt) => {
    try {
      await deleteRestrictedTerm(rt.id);
      setRestrictedTerms((prev) => prev.filter((t) => t.id !== rt.id));
    } catch (err) {
      message.error("Failed to remove restricted term");
    }
  };

  const handleAddCompetitor = async () => {
    const term = newCompetitor.term.trim();
    if (!term) return;
    try {
      const entry = await addCompetitorTerm(term, newCompetitor.reason.trim() || null);
      setCompetitorTerms((prev) => [...prev, entry]);
      setNewCompetitor({ term: '', reason: '' });
      message.success("Competitor term added");
    } catch (err) {
      message.error("Failed to add competitor term");
    }
  };

  const handleRemoveCompetitor = async (ct) => {
    try {
      await deleteCompetitorTerm(ct.id);
      setCompetitorTerms((prev) => prev.filter((t) => t.id !== ct.id));
    } catch (err) {
      message.error("Failed to remove competitor term");
    }
  };

  const handleAddAllowed = async () => {
    const term = newAllowed.term.trim();
    if (!term) return;
    try {
      const entry = await addAllowedTerm(term, newAllowed.reason.trim() || null);
      setAllowedTerms((prev) => [...prev, entry]);
      setNewAllowed({ term: '', reason: '' });
      message.success("Allowed term added");
    } catch (err) {
      message.error("Failed to add allowed term");
    }
  };

  const handleRemoveAllowed = async (at) => {
    try {
      await deleteAllowedTerm(at.id);
      setAllowedTerms((prev) => prev.filter((t) => t.id !== at.id));
    } catch (err) {
      message.error("Failed to remove allowed term");
    }
  };

  if (!settings) {
    return (
      <div className="llm-settings-container">
        <div className="llm-settings-breadcrumb">Polly &rsaquo; <span>Settings</span></div>
        <Spin spinning={loading}><div style={{ minHeight: 200 }} /></Spin>
      </div>
    );
  }

  return (
    <div className="llm-settings-container">
      <div className="llm-settings-breadcrumb">Polly &rsaquo; <span>Settings</span></div>
      <div className="llm-settings-body">
        <div className="llm-settings-header">
          <h2>Polly Settings</h2>
          <Button type="primary" onClick={handleSave} loading={saving} disabled={!dirty}>
            Save Changes
          </Button>
        </div>

        {/* General */}
        <div className="llm-settings-section">
          <div className="llm-settings-section-title">General</div>
          <div className="llm-settings-row">
            <div className="llm-settings-field">
              <div className="llm-settings-label">LLM Conversations</div>
              <div className="llm-settings-hint">Enable Polly AI-powered conversations for your customers</div>
            </div>
            <Switch
              checked={settings.llmEnabled}
              onChange={(val) => update('llmEnabled', val)}
            />
          </div>
        </div>

        {/* Conversation Behavior */}
        <div className="llm-settings-section">
          <div className="llm-settings-section-title">Conversation Behavior</div>

          <div className="llm-settings-row">
            <div className="llm-settings-field">
              <div className="llm-settings-label">Tone and Voice</div>
              <div className="llm-settings-hint">How Polly communicates — affects word choice, formality, and personality</div>
            </div>
            <Input
              value={settings.llmToneVoice || ''}
              onChange={(e) => update('llmToneVoice', e.target.value)}
              placeholder="e.g. friendly and professional"
              style={{ width: 300 }}
            />
          </div>

          <div className="llm-settings-row">
            <div className="llm-settings-field">
              <div className="llm-settings-label">Response Length</div>
              <div className="llm-settings-hint">How detailed Polly's responses should be</div>
            </div>
            <Select
              value={settings.llmResponseLength || 'concise'}
              onChange={(val) => update('llmResponseLength', val)}
              style={{ width: 200 }}
            >
              <Option value="brief">Brief (1-2 sentences)</Option>
              <Option value="concise">Concise (2-4 sentences)</Option>
              <Option value="detailed">Detailed (full explanation)</Option>
            </Select>
          </div>

          <div className="llm-settings-row">
            <div className="llm-settings-field">
              <div className="llm-settings-label">Out-of-Scope Response</div>
              <div className="llm-settings-hint">Message sent when a question is outside Polly's knowledge base. This is stored as a message template. Leave empty to use the default.</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <TextArea
                value={outOfScopeText}
                onChange={(e) => { setOutOfScopeText(e.target.value); setOutOfScopeDirty(true); }}
                placeholder="Default: I'm only able to help with questions about our products and services. For anything else, please reach out to our support team directly."
                rows={3}
                style={{ width: 400 }}
              />
              {outOfScopeDirty && (
                <Button size="small" type="primary" onClick={handleSaveOutOfScope} loading={savingOutOfScope}
                        style={{ alignSelf: 'flex-end' }}>
                  Save Response
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Limits */}
        <div className="llm-settings-section">
          <div className="llm-settings-section-title">Limits</div>

          <div className="llm-settings-row">
            <div className="llm-settings-field">
              <div className="llm-settings-label">Max Message Length</div>
              <div className="llm-settings-hint">Maximum characters per inbound customer message. Messages exceeding this limit are truncated for LLM processing. The full message is still saved for audit.</div>
            </div>
            <InputNumber
              value={settings.maxMessageLength}
              onChange={(val) => update('maxMessageLength', val)}
              placeholder="2500"
              min={100}
              max={10000}
              style={{ width: 150 }}
            />
          </div>

          <div className="llm-settings-row">
            <div className="llm-settings-field">
              <div className="llm-settings-label">Conversation History Window</div>
              <div className="llm-settings-hint">Number of recent messages included as context for the LLM. More history improves continuity but increases response time.</div>
            </div>
            <InputNumber
              value={settings.maxConversationMessages}
              onChange={(val) => update('maxConversationMessages', val)}
              placeholder="10"
              min={2}
              max={50}
              style={{ width: 150 }}
            />
          </div>
        </div>

        {/* RAG Tuning */}
        <div className="llm-settings-section">
          <div className="llm-settings-section-title">Document Retrieval</div>

          <div className="llm-settings-row">
            <div className="llm-settings-field">
              <div className="llm-settings-label">Similarity Threshold</div>
              <div className="llm-settings-hint">Minimum similarity score for a document chunk to be included in the LLM prompt. Higher values return fewer but more relevant chunks.</div>
            </div>
            <Select
              value={settings.ragSimilarityThreshold || undefined}
              onChange={(val) => update('ragSimilarityThreshold', val || null)}
              placeholder="Default (0.65)"
              allowClear
              style={{ width: 200 }}
            >
              <Option value={0.50}>0.50 (Broad)</Option>
              <Option value={0.55}>0.55</Option>
              <Option value={0.60}>0.60</Option>
              <Option value={0.65}>0.65 (Default)</Option>
              <Option value={0.70}>0.70</Option>
              <Option value={0.75}>0.75</Option>
              <Option value={0.80}>0.80 (Strict)</Option>
              <Option value={0.85}>0.85</Option>
              <Option value={0.90}>0.90 (Very strict)</Option>
            </Select>
          </div>

          <div className="llm-settings-row">
            <div className="llm-settings-field">
              <div className="llm-settings-label">Max Document Chunks</div>
              <div className="llm-settings-hint">Maximum number of document chunks to include in the LLM prompt. Fewer chunks = faster responses but narrower context.</div>
            </div>
            <Select
              value={settings.maxRagChunks || undefined}
              onChange={(val) => update('maxRagChunks', val || null)}
              placeholder="Default (5)"
              allowClear
              style={{ width: 200 }}
            >
              <Option value={2}>2 (Minimal)</Option>
              <Option value={3}>3</Option>
              <Option value={5}>5 (Default)</Option>
              <Option value={7}>7</Option>
              <Option value={10}>10 (Maximum)</Option>
            </Select>
          </div>
        </div>

        {/* Access Control */}
        <div className="llm-settings-section">
          <div className="llm-settings-section-title">Access Control</div>

          <div className="llm-settings-row">
            <div className="llm-settings-field">
              <div className="llm-settings-label">Commercial Consent Attribute</div>
              <div className="llm-settings-hint">Controls whether Polly can include commercial content (pricing, promotions, upsells) in responses. When not set or when the contact's value is false, Polly responds with informational content only.</div>
            </div>
            <Select
              showSearch
              allowClear
              value={settings.commercialConsentAttributeId || undefined}
              onChange={(val) => update('commercialConsentAttributeId', val || null)}
              placeholder="Not set (informational responses only)"
              optionFilterProp="children"
              style={{ width: 300 }}
            >
              {attributes.map((attr) => (
                <Option key={attr.id} value={attr.id}>{attr.name}</Option>
              ))}
            </Select>
          </div>

        </div>

        {/* Restricted Terms */}
        <div className="llm-settings-section">
          <div className="llm-settings-section-title">Restricted Terms</div>
          <div className="llm-settings-hint" style={{ marginBottom: 8 }}>
            Terms that Polly must never use in responses. Violations trigger rephrasing or blocking.
          </div>
          {restrictedTerms.length > 0 ? (
            <div className="llm-settings-chip-list">
              {restrictedTerms.map((rt) => (
                <span key={rt.id} className="llm-settings-chip">
                  {rt.term}
                  {rt.reason && <span className="llm-settings-chip-reason" title={rt.reason}>({rt.reason})</span>}
                  <button className="llm-settings-chip-remove" onClick={() => handleRemoveRestricted(rt)}>&times;</button>
                </span>
              ))}
            </div>
          ) : (
            <div className="llm-settings-empty">No restricted terms configured.</div>
          )}
          <div className="llm-settings-add-row">
            <Input
              value={newRestricted.term}
              onChange={(e) => setNewRestricted((prev) => ({ ...prev, term: e.target.value }))}
              placeholder="Term"
              style={{ flex: 2 }}
              onPressEnter={handleAddRestricted}
            />
            <Input
              value={newRestricted.reason}
              onChange={(e) => setNewRestricted((prev) => ({ ...prev, reason: e.target.value }))}
              placeholder="Reason (optional)"
              style={{ flex: 3 }}
              onPressEnter={handleAddRestricted}
            />
            <Button onClick={handleAddRestricted} disabled={!newRestricted.term.trim()}>Add</Button>
          </div>
        </div>

        {/* Competitor Terms */}
        <div className="llm-settings-section">
          <div className="llm-settings-section-title">Competitor Terms</div>
          <div className="llm-settings-hint" style={{ marginBottom: 8 }}>
            Competitor terms that Polly should avoid mentioning.
          </div>
          {competitorTerms.length > 0 ? (
            <div className="llm-settings-chip-list">
              {competitorTerms.map((ct) => (
                <span key={ct.id} className="llm-settings-chip">
                  {ct.term}
                  {ct.reason && <span className="llm-settings-chip-reason" title={ct.reason}>({ct.reason})</span>}
                  <button className="llm-settings-chip-remove" onClick={() => handleRemoveCompetitor(ct)}>&times;</button>
                </span>
              ))}
            </div>
          ) : (
            <div className="llm-settings-empty">No competitor terms configured.</div>
          )}
          <div className="llm-settings-add-row">
            <Input
              value={newCompetitor.term}
              onChange={(e) => setNewCompetitor((prev) => ({ ...prev, term: e.target.value }))}
              placeholder="Term"
              style={{ flex: 2 }}
              onPressEnter={handleAddCompetitor}
            />
            <Input
              value={newCompetitor.reason}
              onChange={(e) => setNewCompetitor((prev) => ({ ...prev, reason: e.target.value }))}
              placeholder="Reason (optional)"
              style={{ flex: 3 }}
              onPressEnter={handleAddCompetitor}
            />
            <Button onClick={handleAddCompetitor} disabled={!newCompetitor.term.trim()}>Add</Button>
          </div>
        </div>

        {/* Allowed Terms (PII whitelist) */}
        <div className="llm-settings-section">
          <div className="llm-settings-section-title">Allowed Terms</div>
          <div className="llm-settings-hint" style={{ marginBottom: 8 }}>
            Values that should not be redacted by PII detection, such as your support phone number or email address. Phone numbers are normalized for matching regardless of format.
          </div>
          {allowedTerms.length > 0 ? (
            <div className="llm-settings-chip-list">
              {allowedTerms.map((at) => (
                <span key={at.id} className="llm-settings-chip">
                  {at.term}
                  {at.reason && <span className="llm-settings-chip-reason" title={at.reason}>({at.reason})</span>}
                  <button className="llm-settings-chip-remove" onClick={() => handleRemoveAllowed(at)}>&times;</button>
                </span>
              ))}
            </div>
          ) : (
            <div className="llm-settings-empty">No allowed terms configured.</div>
          )}
          <div className="llm-settings-add-row">
            <Input
              value={newAllowed.term}
              onChange={(e) => setNewAllowed((prev) => ({ ...prev, term: e.target.value }))}
              placeholder="e.g. +15551234567 or support@acme.com"
              style={{ flex: 2 }}
              onPressEnter={handleAddAllowed}
            />
            <Input
              value={newAllowed.reason}
              onChange={(e) => setNewAllowed((prev) => ({ ...prev, reason: e.target.value }))}
              placeholder="Reason (optional)"
              style={{ flex: 3 }}
              onPressEnter={handleAddAllowed}
            />
            <Button onClick={handleAddAllowed} disabled={!newAllowed.term.trim()}>Add</Button>
          </div>
        </div>

      </div>
    </div>
  );
}
